import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import { WB_W, WB_H, WhiteboardCanvas } from './whiteboardShapes';

const PAD = 12;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function fitView(vw, vh) {
  const s = Math.min((vw - PAD * 2) / WB_W, (vh - PAD * 2) / WB_H);
  return { s, x: (vw - WB_W * s) / 2, y: (vh - WB_H * s) / 2 };
}

// Keep at least `keep` px of the board on screen so it can never be lost.
function bound(v, vw, vh) {
  const w = WB_W * v.s, h = WB_H * v.s, kx = Math.min(120, w / 2), ky = Math.min(120, h / 2);
  return { s: v.s, x: clamp(v.x, kx - w, vw - kx), y: clamp(v.y, ky - h, vh - ky) };
}

function CtrlBtn({ onClick, label, testid, children }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testid}
      className="h-11 min-w-11 px-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 active:bg-white/30 transition-colors">
      {children}
    </button>
  );
}

// Read-only full-screen viewer: transforms only the view (zoom/pan); never touches the objects.
export function WhiteboardFullscreen({ content, onClose }) {
  const drawnTitle = (Array.isArray(content.objects) ? content.objects : [])
    .filter((o) => o.type === 'text' && !o.hidden && String(o.text || '').trim())
    .sort((a, b) => (b.fontSize || 0) - (a.fontSize || 0))[0];
  const title = content.title || (drawnTitle ? String(drawnTitle.text).split('\n')[0] : 'Whiteboard');
  const stageRef = useRef(null);
  const ptrs = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState(null);
  const fit = size.w ? fitView(size.w, size.h) : null;
  const minS = fit ? fit.s * 0.75 : 0.1, maxS = fit ? Math.max(fit.s * 8, 4) : 8;

  const setBounded = useCallback((v) => setView(bound({ ...v, s: clamp(v.s, minS, maxS) }, size.w, size.h)), [minS, maxS, size]);
  const zoomAt = (factor, px, py) => {
    const s = clamp(view.s * factor, minS, maxS), k = s / view.s;
    setBounded({ s, x: px - (px - view.x) * k, y: py - (py - view.y) * k });
  };
  const reset = () => fit && setView(fit);

  useLayoutEffect(() => {
    const measure = () => {
      const r = stageRef.current.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
      setView(fitView(r.width, r.height)); // re-fit on open, resize and orientation change
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => { window.removeEventListener('resize', measure); window.removeEventListener('orientationchange', measure); };
  }, []);

  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const prev = [html.style.overflow, body.style.overflow, body.style.overscrollBehavior];
    html.style.overflow = 'hidden'; body.style.overflow = 'hidden'; body.style.overscrollBehavior = 'none';
    // Capture phase: page-level shortcuts (e.g. presentation Esc / F) must not fire while the viewer is open.
    const onKey = (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      [html.style.overflow, body.style.overflow, body.style.overscrollBehavior] = prev;
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  useEffect(() => {
    const el = stageRef.current;
    const onWheel = (e) => { // non-passive so the page never scrolls under the viewer
      e.preventDefault();
      if (!view) return;
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0025)), e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  const local = (e) => { const r = stageRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const startGesture = () => {
    const pts = [...ptrs.current.values()];
    if (pts.length >= 2) {
      const [a, b] = pts;
      gesture.current = { kind: 'pinch', d: Math.hypot(a.x - b.x, a.y - b.y) || 1, m: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, v: view };
    } else if (pts.length === 1) {
      gesture.current = { kind: 'pan', p: pts[0], v: view };
    } else gesture.current = null;
  };
  const onDown = (e) => {
    e.preventDefault();
    if (!view || !fit) return;
    try { stageRef.current.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    const p = local(e);
    ptrs.current.set(e.pointerId, p);
    const now = Date.now();
    if (ptrs.current.size === 1 && now - lastTap.current < 300) { // double-tap / double-click: zoom in, or fit if zoomed
      lastTap.current = 0;
      if (view.s > fit.s * 1.5) reset(); else zoomAt(2.5, p.x, p.y);
      gesture.current = null;
      return;
    }
    lastTap.current = ptrs.current.size === 1 ? now : 0;
    startGesture();
  };
  const onMove = (e) => {
    if (!ptrs.current.has(e.pointerId) || !gesture.current) return;
    ptrs.current.set(e.pointerId, local(e));
    const g = gesture.current, pts = [...ptrs.current.values()];
    if (g.kind === 'pan') {
      const p = pts[0];
      if (Math.hypot(p.x - g.p.x, p.y - g.p.y) > 6) lastTap.current = 0;
      setBounded({ s: g.v.s, x: g.v.x + p.x - g.p.x, y: g.v.y + p.y - g.p.y });
    } else if (pts.length >= 2) {
      const [a, b] = pts;
      const d = Math.hypot(a.x - b.x, a.y - b.y), m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const s = clamp(g.v.s * (d / g.d), minS, maxS);
      const cx = (g.m.x - g.v.x) / g.v.s, cy = (g.m.y - g.v.y) / g.v.s; // board point under the original focal point
      setBounded({ s, x: m.x - cx * s, y: m.y - cy * s });
    }
  };
  const onUp = (e) => { ptrs.current.delete(e.pointerId); startGesture(); };

  const zoomCenter = (f) => view && zoomAt(f, size.w / 2, size.h / 2);
  const pct = view && fit ? Math.round((view.s / fit.s) * 100) : 100;

  return createPortal(
    <div data-testid="wb-fullscreen" role="dialog" aria-modal="true" aria-label={title}
      className="fixed inset-0 z-[10000] flex flex-col bg-slate-950 text-white"
      style={{ height: '100dvh', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <p className="flex-1 min-w-0 truncate text-sm font-medium text-white/90" data-testid="wb-fullscreen-title">{title}</p>
        <span className="text-xs tabular-nums text-white/60 w-12 text-right" data-testid="wb-fullscreen-zoom">{pct}%</span>
        <CtrlBtn onClick={() => zoomCenter(1 / 1.4)} label="Zoom out" testid="wb-fullscreen-zoom-out"><ZoomOut className="h-5 w-5" /></CtrlBtn>
        <CtrlBtn onClick={() => zoomCenter(1.4)} label="Zoom in" testid="wb-fullscreen-zoom-in"><ZoomIn className="h-5 w-5" /></CtrlBtn>
        <CtrlBtn onClick={reset} label="Fit to screen" testid="wb-fullscreen-fit"><Maximize className="h-5 w-5" /><span className="hidden sm:inline">Fit</span></CtrlBtn>
        <CtrlBtn onClick={onClose} label="Close full screen" testid="wb-fullscreen-close"><X className="h-5 w-5" /><span className="hidden sm:inline">Close</span></CtrlBtn>
      </div>
      <div ref={stageRef} data-testid="wb-fullscreen-stage" className="relative flex-1 overflow-hidden select-none"
        style={{ touchAction: 'none', cursor: gesture.current?.kind === 'pan' ? 'grabbing' : 'grab', overscrollBehavior: 'contain' }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        {view && (
          <div data-testid="wb-fullscreen-board" className="absolute left-0 top-0 rounded-lg overflow-hidden shadow-2xl"
            style={{ left: view.x, top: view.y, width: WB_W * view.s, height: WB_H * view.s,
              backgroundColor: content.background || '#FFFFFF' }}>
            <WhiteboardCanvas content={content} className="w-full h-full block pointer-events-none" />
          </div>
        )}
      </div>
      <p className="px-3 py-1.5 text-center text-[11px] text-white/50">Read-only · pinch or scroll to zoom · drag to pan · double-tap to zoom</p>
    </div>,
    document.body,
  );
}
